import React, { useState, useEffect, useRef } from "react";
import queryString from "query-string";
import { useSelector, useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { Cache } from "aws-amplify/utils";
import "react-phone-number-input/style.css";
import {
  register,
  resendConfirmationCode,
  confirmRegistration,
} from "../../actions/auth_actions";
import { AUTH_ERROR_CLEAR } from "../../actions/types";
import { Button, IconButton, InputAdornment, LinearProgress, Link, Paper, Snackbar, SnackbarContent, TextField, Typography, CircularProgress, Box, Grid, Divider, Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery, useTheme } from "@mui/material";
import { styled } from '@mui/material/styles';
import { green } from "@mui/material/colors";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Content from "../Privacy/Content";
import { ReactComponent as SmLogo } from "../Common/Logo/smLogo.svg";
import { ReactComponent as Mountains } from "./Icons/mountains.svg";
import ReCAPTCHA from "react-google-recaptcha";
import config from "../../config";
import * as ROUTES from "../../constants/routes";

// --- Styled Components to match Login.js ---

const RootContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  minWidth: 720,
  maxWidth: 720,
  [theme.breakpoints.down(740)]: {
    maxWidth: 600,
    minWidth: 600,
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
    minHeight: '100vh',
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(4),
  marginRight: '27px',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2),
  },
}));

const MountainsGrid = styled(Grid)(({ theme }) => ({
    backgroundColor: "#FDEFE7",
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    overflow: "hidden",
    display: "flex", // Keep this for alignment
    '& .mountains-svg': {
        width: "105%",
        marginLeft: -1,
        alignSelf: "flex-end",
        marginBottom: -15,
    }
}));

const Signup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const recaptchaRef = useRef(null);

  const { error:errorMessage, needConfirm, success, username } = useSelector(state => state.auth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [cpassword, setCpassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [open, setOpen] = useState(false);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [openPrivacy, setOpenPrivacy] = useState(false);
  const [org, setOrg] = useState(false);

  useEffect(() => {
    let url = location.search;
    let params = queryString.parse(url);

    if (params.origin) {
      try {
        const k = params.origin.split(":");
        const [org, provider, contract] = k;

        if (org && provider && contract) {
          Cache.setItem("origin", {
            organisation: org,
            provider: provider,
            contract: contract,
          });
          window.history.pushState({}, document.title, window.location.pathname);
          setOrg(org);
        }
      } catch (e) {
        //console.log("origin not correct - ignore");
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (errorMessage && errorMessage !== "") {
      setOpen(true);
      setError(errorMessage);
      setLoading(false);
    }
  }, [errorMessage]);

  useEffect(() => {
    setLoading(false);
  }, [needConfirm]);

  const handleClose = () => {
    setOpen(false);
    dispatch({ type: AUTH_ERROR_CLEAR });
  };

  const handleChange = (prop) => (event) => {
    let v = event.target.value;
    if (prop === "firstname" || prop === "lastname") {
      v = v.trimStart();
    }
    if (prop === "email") setEmail(v);
    if (prop === "password") setPassword(v);
    if (prop === "cpassword") setCpassword(v);
    if (prop === "firstname") setFirstname(v);
    if (prop === "lastname") setLastname(v);
    if (prop === "code") setCode(v);
  };

  const handleChangeMobile = (value) => {
    setMobile(value);
  };

  const handleCloseDialog = () => {
    setOpenPrivacy(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword((state) => !state);
  };

  const registerUser = (e) => {
    e.preventDefault();

    let username = email.trim().toLowerCase();
    let recaptchaValue = recaptchaRef.current.getValue();

    setLoading(true);
    setError("");
    setOpen(false);

    if (password === cpassword) {
      if (recaptchaValue) {
        dispatch(
          register(
            {
              username: username,
              password: password,
              email: username,
              phone: mobile?.trim(),
              given_name: firstname.trim(),
              family_name: lastname.trim(),
              captcha: recaptchaValue,
            },
            navigate
          )
        );
      } else {
        setLoading(false);
        setOpen(true);
        setError("Please use the captcha.");
      }
    } else {
      setLoading(false);
      setOpen(true);
      setError("Passwords should match");
    }
  };

  const confirmCode = (e) => {
    e.preventDefault();
    dispatch(
      confirmRegistration(
        { username: username, code: code },
        navigate
      )
    );
  };

  const resendCode = (e) => {
    dispatch(
      resendConfirmationCode(
        username,
        navigate
      )
    );
  };

  const isRightLength = password && password.length >= 8;
  const hasSpecial = /[/\W|_/g]/.test(password);
  const hasNumber = password.match(/\d/);
  const hasUpperCase = password.match(/[A-Z]/);

  const textFieldProps = {
    fullWidth: true,
    size: "small",
    variant: "outlined",
    InputProps: {
        sx: { fontSize: 14, backgroundColor: "#F5F6F7", '& .MuiOutlinedInput-notchedOutline': { border: 'none' } },
        style: { border: "none" },
    },
    sx: { mt: 0, mb: 0 }
  };

  return (
    <RootContainer>
      <Box sx={{ height: { xs: 2, md: 3 } }}>
        {loading && <LinearProgress sx={{ height: "100%" }} />}
      </Box>

      <Grid container spacing={0} direction="column" justifyContent="center" alignItems="center" sx={{ minHeight: "100vh" }}>
        <Grid item sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            {org && org !== "skillzminer" ? (
                <Box component="img" src={`${config.STORAGE_CLOUDFRONT_URL}/logos/${org}_appnav_logo.png`}
                    sx={{
                        maxHeight: { sm: 160 },
                        width: { xs: '80%', sm: '80%' },
                        mt: { xs: '15px', sm: 0 },
                    }}
                />
            ) : (
              <Box sx={{
                display: 'flex', // Ensures the Box behaves like a container for the SVG
                justifyContent: 'center', // Example alignment
                maxHeight: { sm: 160 },
                width: { xs: '80%', sm: '100%' }, // Apply responsive width to the container
                mt: { xs: '15px', sm: 0 },
            }}>
                <SmLogo style={{ width: '100%', height: 'auto' }} />
            </Box>
            )}
        </Grid>
        <Grid item sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
          <LoginPaper>
            <Grid container spacing={0} direction="row-reverse" justifyContent="center" alignItems="stretch" sx={{ minHeight: 450 }}>
              <MountainsGrid item size={{ sm: 5 }} sx={{ display: { xs: 'none', sm: 'flex' }}}>
                  <Mountains className="mountains-svg" sx={{width: {sm:"100%"} }}/>
              </MountainsGrid>
              <Grid item size={{ xs: 12, sm: 7 }}>
                {!needConfirm && (
                  <FormContainer>
                    <Typography variant="h4" sx={{ fontSize: { xs: 25, sm: 32 } }}>
                      Sign up
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#5A6375", fontSize: 14, mt: 0.375, mb: 2 }}>
                      If you have been sent an invitation to join our platform by a 3rd party, please use the link provided in that email.
                    </Typography>
                    {!org && (
                      <Typography variant="body1" sx={{ color: "#5A6375", fontSize: 14, mt: 0.375, mb: 2 }}>
                        If you discovered Skillsminer via a QR code or link and haven't signed up yet, please re-scan the QR code or re-click the link
                      </Typography>
                    )}
                    <Box component="form" sx={{ width: "100%" }} onSubmit={registerUser}>
                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        First name
                      </Typography>
                      <TextField {...textFieldProps} id="outlined-firstname-input" type="text" name="firstname" autoComplete="given-name" placeholder="John" data-test="signup-firstname" required value={firstname} onChange={handleChange("firstname")} />

                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        Last name
                      </Typography>
                      <TextField {...textFieldProps} id="outlined-lastname-input" type="text" name="lastname" autoComplete="family-name" placeholder="Doe" data-test="signup-lastname" required value={lastname} onChange={handleChange("lastname")} />

                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        Email
                      </Typography>
                      <TextField {...textFieldProps} id="outlined-email-input" type="email" name="email" autoComplete="email" placeholder="name@email.com" data-test="signup-email" required value={email} onChange={handleChange("email")} />

                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        Mobile number
                      </Typography>
                      <PhoneInput
                        id="outlined-phone-input"
                        placeholder="Enter phone number"
                        defaultCountry="GB"
                        value={mobile}
                        international
                        flags={flags}
                        onChange={handleChangeMobile}
                      />

                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        Password
                      </Typography>
                      {password.length > 0 && (
                        <Paper sx={{ mb: "5px", p: 1, backgroundColor: "#faebd7" }}>
                          <Grid container>
                              {[
                                  { label: '1 uppercase character', valid: hasUpperCase }, { label: '1 number', valid: hasNumber },
                                  { label: '1 symbol', valid: hasSpecial }, { label: '8 characters minimum', valid: isRightLength },
                              ].map(rule => (
                                  <Grid item size={{xs:6}} key={rule.label}>
                                      <Typography variant="body2" sx={{ color: "#5A6375", fontSize: 12, textAlign: "left", display: 'flex', alignItems: 'center' }}>
                                          {rule.valid ? <CheckCircleIcon sx={{ color: "#69cc63", mr: 0.5 }} /> : <CancelIcon sx={{ color: "#ff4c4c", mr: 0.5 }} />} {rule.label}
                                      </Typography>
                                  </Grid>
                              ))}
                          </Grid>
                        </Paper>
                      )}
                      <TextField
                        {...textFieldProps}
                        id="password"
                        placeholder="Enter your password"
                        autoComplete="off"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={handleChange("password")}
                        InputProps={{
                          ...textFieldProps.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton aria-label="Toggle password visibility" onClick={handleClickShowPassword}>
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        required
                      />
                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        Confirm password
                      </Typography>
                      <TextField
                        {...textFieldProps}
                        id="cpassword"
                        placeholder="Confirm password"
                        autoComplete="off"
                        type={showPassword ? "text" : "password"}
                        value={cpassword}
                        onChange={handleChange("cpassword")}
                        InputProps={{
                          ...textFieldProps.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton aria-label="Toggle password visibility" onClick={handleClickShowPassword}>
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        required
                      />
                      <ReCAPTCHA
                        style={{ marginTop: 15 }}
                        ref={recaptchaRef}
                        sitekey="6LfkUdoaAAAAACWTV3biAMmcp8JLPQpsuWzrFDMa"
                      />
                      <Typography variant="body2" sx={{ color: "#5A6375", fontSize: 12, mt: 2, textAlign: "left" }}>
                        By signing up, I acknowledge the{" "}
                        <Link id="privacy-policy-link" href="https://www.skillsminer.ai/privacy-policy/" target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </Link>
                        {" "} and {" "}
                        <Link id="terms-link" href="https://www.skillsminer.ai/terms-of-use/" target="_blank" rel="noopener noreferrer">
                          Terms of use
                        </Link>
                        .
                      </Typography>

                      <Box sx={{ position: "relative" }}>
                        <Button
                          variant="contained"
                          sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: '14px', sm: 2 } }}
                          color="primary"
                          type="submit"
                          fullWidth
                          disabled={loading}
                        >
                          Sign up
                        </Button>
                        {loading && (
                          <CircularProgress size={24} sx={{ position: "absolute", top: "50%", left: "50%", mt: -1.5, ml: -1.5 }} />
                        )}
                      </Box>
                    </Box>
                    <Divider />
                    <Typography variant="body2" sx={{ color: "#5A6375", fontSize: 14, mt: '14px', textAlign: "center" }}>
                      Already have a Skillsminer account?{" "}
                      <Link id="sign-in-btn" component={RouterLink} to={ROUTES.LOGIN}>
                        Sign In
                      </Link>
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#5A6375", fontSize: 14, mt: '14px', textAlign: "center" }}>
                      <Link id="youtube-link" href="https://www.youtube.com/watch?v=1u9GtcsA5z4&list=PLaqkddI5TjYUXqGx-0rFFF2vZVTqYc2qp/" target="_blank" rel="noopener noreferrer">
                        Visit
                      </Link>
                      {" "} our Youtube Channel
                    </Typography>
                  </FormContainer>
                )}

                {needConfirm && (
                  <FormContainer>
                    <Typography variant="h4" sx={{ fontSize: { xs: 25, sm: 32 } }}>
                      Verify your email
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#5A6375", fontSize: 14, mt: 0.375, mb: 2 }}>
                      Thanks for signing up for Skillsminer. You’re just a couple of steps away from unearthing your potential and discovering a world of jobs you never knew you could do. Please verify your email address by entering the confirmation code we sent to {" "}
                      <strong>{username}</strong>
                    </Typography>
                    <Box component="form" sx={{ width: "100%" }} onSubmit={confirmCode}>
                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        Confirmation code
                      </Typography>
                      <TextField {...textFieldProps} id="outlined-code-input" type="text" name="code" autoComplete="off" placeholder="Code" data-test="signup-code" required onChange={handleChange("code")} />

                      <Button
                        variant="contained"
                        sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: '14px', sm: 2 } }}
                        color="primary"
                        type="submit"
                        fullWidth
                      >
                        Verify
                      </Button>
                    </Box>
                    <Typography variant="body1" sx={{ color: "#5A6375", fontSize: 14, mt: 0.375, mb: 2 }}>
                      If you haven't received your confirmation code, please check your spam folder. If it hasn't arrived or has expired, you can request a new code using the button below.
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: '14px', sm: 2 } }}
                      color="default"
                      fullWidth
                      onClick={resendCode}
                    >
                      Resend Code
                    </Button>
                  </FormContainer>
                )}
                {org && org !== "skillzminer" && (
                  <Grid container spacing={0} direction="column" justifyContent="center" alignItems="center">
                    <Grid item>
                      <Box component="img" src={`${config.STORAGE_CLOUDFRONT_URL}/logos/${org}_powered_by_logo.png`} sx={{ height: 40, width: 165, mb: 2.5 }} />
                    </Grid>
                  </Grid>
                )}
                <Box sx={{ fontSize: 9, color: "#bbb", textAlign: "right", mt: '3px', mr: 2, mb: '5px' }}>{config.version}</Box>
              </Grid>
            </Grid>
          </LoginPaper>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={open}
        sx={{ mt: '10px' }}
        onClose={handleClose}
        autoHideDuration={3000}
        ContentProps={{ "aria-describedby": "message-id" }}
      >
        <SnackbarContent
          sx={{ backgroundColor: theme.palette.error.dark }}
          aria-describedby="client-snackbar"
          message={<span id="message-id">{error}</span>}
        />
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={success}
        sx={{ mt: '10px' }}
        onClose={handleClose}
        autoHideDuration={3000}
        ContentProps={{ "aria-describedby": "message-id" }}
      >
        <SnackbarContent
          sx={{ backgroundColor: green[600] }}
          aria-describedby="client-snackbar"
          message={<span id="message-id">{success}</span>}
        />
      </Snackbar>

      <Dialog
        open={openPrivacy}
        onClose={handleCloseDialog}
        scroll="paper"
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        fullWidth={true}
        maxWidth={"md"}
        fullScreen={fullScreen}
      >
        <DialogTitle id="scroll-dialog-title">Privacy Policy</DialogTitle>
        <DialogContent dividers={true}>
          <Content />
        </DialogContent>
        <DialogActions>
          <Button id="privacy-policy-close" onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </RootContainer>
  );
};

export default Signup;